import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

export interface NotificationData {
  id: string
  type: 'success' | 'error' | 'warning' | 'info' | 'confirm'
  title: string
  message: string
  duration?: number // Auto-dismiss after this many ms (0 = no auto-dismiss)
  onConfirm?: () => void
  onCancel?: () => void
  confirmText?: string
  cancelText?: string
}

interface CustomNotificationProps {
  notifications: NotificationData[]
  onDismiss: (id: string) => void
  onConfirm: (id: string) => void
  onCancel: (id: string) => void
}

const CustomNotification = ({ notifications, onDismiss, onConfirm, onCancel }: CustomNotificationProps) => {
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set())

  // Auto-dismiss notifications with duration
  useEffect(() => {
    notifications.forEach(notification => {
      if (notification.duration && notification.duration > 0 && notification.type !== 'confirm') {
        const timer = setTimeout(() => {
          handleDismiss(notification.id)
        }, notification.duration)

        return () => clearTimeout(timer)
      }
    })
  }, [notifications])

  const handleDismiss = (id: string) => {
    setDismissingIds(prev => new Set(prev).add(id))
    setTimeout(() => {
      onDismiss(id)
      setDismissingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(id)
        return newSet
      })
    }, 200)
  }

  const handleConfirm = (id: string) => {
    onConfirm(id)
  }

  const handleCancel = (id: string) => {
    onCancel(id)
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-8 h-8 text-emerald-400" />
      case 'error':
        return <XCircleIcon className="w-8 h-8 text-red-400" />
      case 'warning':
        return <ExclamationTriangleIcon className="w-8 h-8 text-yellow-400" />
      case 'confirm':
        return <ExclamationTriangleIcon className="w-8 h-8 text-yellow-400" />
      default:
        return <InformationCircleIcon className="w-8 h-8 text-blue-400" />
    }
  }

  const getColors = (type: string) => {
    switch (type) {
      case 'success':
        return {
          iconBg: 'bg-emerald-500/20',
          border: 'border-emerald-500/30',
          accent: 'text-emerald-400'
        }
      case 'error':
        return {
          iconBg: 'bg-red-500/20',
          border: 'border-red-500/30',
          accent: 'text-red-400'
        }
      case 'warning':
      case 'confirm':
        return {
          iconBg: 'bg-yellow-500/20',
          border: 'border-yellow-500/30',
          accent: 'text-yellow-400'
        }
      default:
        return {
          iconBg: 'bg-blue-500/20',
          border: 'border-blue-500/30',
          accent: 'text-blue-400'
        }
    }
  }

  if (notifications.length === 0) return null

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Toast notifications (top-right) */}
      <div className="absolute top-4 right-4 space-y-3 max-w-sm w-full">
        <AnimatePresence>
          {notifications
            .filter(n => n.type !== 'confirm')
            .map(notification => {
              const colors = getColors(notification.type)
              const isDismissing = dismissingIds.has(notification.id)
              
              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, x: 300, scale: 0.9 }}
                  animate={{ 
                    opacity: isDismissing ? 0 : 1, 
                    x: isDismissing ? 300 : 0, 
                    scale: isDismissing ? 0.9 : 1 
                  }}
                  exit={{ opacity: 0, x: 300, scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className={`glass-mockup rounded-2xl p-4 pointer-events-auto border ${colors.border}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-10 h-10 rounded-full ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
                      {getIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold ${colors.accent} text-sm`}>
                        {notification.title}
                      </h4>
                      <p className="text-gray-300 text-sm mt-1">
                        {notification.message}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => handleDismiss(notification.id)}
                      className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )
            })}
        </AnimatePresence>
      </div>

      {/* Confirmation modals (center screen) */}
      <AnimatePresence>
        {notifications
          .filter(n => n.type === 'confirm')
          .map(notification => {
            const colors = getColors(notification.type)
            
            return (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm pointer-events-auto"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className={`glass-mockup rounded-2xl p-8 max-w-md mx-4 text-center border ${colors.border}`}
                >
                  <div className="flex justify-center mb-4">
                    <div className={`w-16 h-16 ${colors.iconBg} rounded-full flex items-center justify-center`}>
                      {getIcon(notification.type)}
                    </div>
                  </div>
                  
                  <h3 className={`text-xl font-bold ${colors.accent} mb-2`}>
                    {notification.title}
                  </h3>
                  
                  <p className="text-gray-300 mb-6">
                    {notification.message}
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleCancel(notification.id)}
                      className="flex-1 px-4 py-2 bg-gray-500/20 border border-gray-500/30 rounded-lg text-gray-300 hover:bg-gray-500/30 transition-colors"
                    >
                      {notification.cancelText || 'Cancel'}
                    </button>
                    <button
                      onClick={() => handleConfirm(notification.id)}
                      className="flex-1 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors"
                    >
                      {notification.confirmText || 'Confirm'}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )
          })}
      </AnimatePresence>
    </div>
  )
}

export default CustomNotification 